import React from "react";
import { Alert, Box, Button, Card, Dropdown, Stack, Text } from "braid-design-system";
import { claimStation, getStations, StationOption } from "../services/signOnService";
import { ensureTerminalId, setStationId } from "../utils/terminalStation";

interface TerminalSetupProps {
    onComplete: (stationId: string) => void;
}

const TerminalSetup: React.FC<TerminalSetupProps> = ({ onComplete }) => {
    const terminalId = React.useMemo(() => ensureTerminalId(), []);
    const [stations, setStations] = React.useState<StationOption[]>([]);
    const [infoMessage, setInfoMessage] = React.useState<string | null>(null);
    const [selectedId, setSelectedId] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const loadStations = React.useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await getStations(terminalId);
            setStations(result.stations);
            // Only surface why the list is empty (no branch / no counters for
            // the user's branch), not the routine success message.
            setInfoMessage(result.stations.length === 0 ? result.message || null : null);
        } catch (error: any) {
            setErrorMessage(error.message || "Failed to load stations");
        } finally {
            setIsLoading(false);
        }
    }, [terminalId]);

    React.useEffect(() => {
        loadStations();
    }, [loadStations]);

    const selectedStation = stations.find((station) => String(station.id) === selectedId) ?? null;

    const handleSave = async () => {
        if (!selectedStation) {
            setErrorMessage("Please select a station for this terminal");
            return;
        }
        setErrorMessage(null);
        setIsSaving(true);
        try {
            await claimStation(selectedStation.id, terminalId);
            setStationId(selectedStation.station_id);
            onComplete(selectedStation.station_id);
        } catch (error: any) {
            // Someone else claimed it first (or it's otherwise no longer
            // available) - refresh so the picker reflects reality.
            setErrorMessage(error.message || "Failed to assign this station to the terminal");
            await loadStations();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box padding="large" style={{ maxWidth: 480, margin: "0 auto" }}>
            <Card>
                <Box padding="medium">
                    <Stack space="medium">
                        <Text weight="strong" size="large">Terminal Setup</Text>
                        <Text tone="secondary">
                            Select which station this terminal is. This only needs to be
                            done once per computer.
                        </Text>

                        <Dropdown
                            label="Station"
                            value={selectedId}
                            disabled={isLoading || isSaving || stations.length === 0}
                            onChange={(e) => setSelectedId((e.target as HTMLSelectElement).value)}
                        >
                            {[
                                <option key="__placeholder__" value="">
                                    {isLoading ? "Loading stations..." : "Select a station"}
                                </option>,
                                ...stations.map((station) => (
                                    <option
                                        key={station.id}
                                        value={String(station.id)}
                                        disabled={station.is_assigned_elsewhere}
                                    >
                                        {station.station_id}
                                        {station.station_loc ? ` (${station.station_loc})` : ""}
                                        {station.is_assigned_elsewhere
                                            ? " — assigned to another terminal"
                                            : station.is_occupied
                                            ? " — in use"
                                            : ""}
                                    </option>
                                )),
                            ]}
                        </Dropdown>

                        {selectedStation?.is_occupied ? (
                            <Alert tone="caution">
                                <Text>
                                    Station {selectedStation.station_id} already has an active
                                    sign-on{selectedStation.occupied_by ? ` (${selectedStation.occupied_by})` : ""}.
                                    Only continue if you're sure no other terminal is using it.
                                </Text>
                            </Alert>
                        ) : null}

                        {infoMessage ? (
                            <Alert tone="info">
                                <Text>{infoMessage}</Text>
                            </Alert>
                        ) : null}

                        <Box>
                            <div style={{ width: "160px" }}>
                                <Button variant="solid" tone="brandAccent" onClick={handleSave} loading={isLoading || isSaving}>
                                    Save & Continue
                                </Button>
                            </div>
                        </Box>

                        {errorMessage ? (
                            <Alert tone="critical">
                                <Text>{errorMessage}</Text>
                            </Alert>
                        ) : null}
                    </Stack>
                </Box>
            </Card>
        </Box>
    );
};

export default TerminalSetup;
